import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LEGACY_TOKEN_KEY = 'adminToken'; // kept for one-release grace period
const CSRF_COOKIE_NAME = 'zog_csrf';

// ─── Global axios interceptor: auto-attach X-CSRF-Token to any credentialed request ───
// PRIMARY source: an in-memory token published by /api/admin/me on every
// successful auth check. This is reliable even when the browser holds stale
// duplicate zog_csrf cookies (which was the silent cause of intermittent
// "Security check failed (CSRF)" errors on admin write actions).
// FALLBACK: the document.cookie value, for the brief window before /me runs.
let _inMemoryCsrf = '';
export const setCsrfToken = (token) => {
  _inMemoryCsrf = token || '';
  // Also poke it into window for cross-tab diagnostics in dev tools.
  if (typeof window !== 'undefined') window.__zogCsrf = _inMemoryCsrf;
};

const _readCsrfCookie = () => {
  if (_inMemoryCsrf) return _inMemoryCsrf;
  try {
    // If multiple zog_csrf cookies exist (path/domain accumulations) the
    // browser sends them in order — the *first* one is what the backend
    // reads via request.cookies.get(), so we match the FIRST occurrence too.
    const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${CSRF_COOKIE_NAME}=([^;]+)`));
    return m ? decodeURIComponent(m[1]) : '';
  } catch {
    return '';
  }
};

if (typeof window !== 'undefined' && !window.__zogAxiosCsrfInstalled) {
  axios.interceptors.request.use((config) => {
    if (config.withCredentials) {
      const tok = _readCsrfCookie();
      if (tok) {
        config.headers = config.headers || {};
        config.headers['X-CSRF-Token'] = tok;
      }
    }
    return config;
  });
  window.__zogAxiosCsrfInstalled = true;
}

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * AuthContext — httpOnly-cookie–first.
 *
 * The backend sets a `zog_admin` httpOnly cookie on /api/admin/login.
 * The cookie is the source of truth; we read auth state from /api/admin/me.
 * We no longer store the JWT in localStorage. The `token` field is kept
 * (always null) for one release to avoid breaking older admin components
 * that still call `useAuth().token`. Any pre-existing localStorage value
 * is purged on mount to remove stale tokens.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Resolve current admin from cookie session
  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/me`, {
        withCredentials: true,
      });
      setUser(response.data);
      // /me re-issues the CSRF cookie AND returns the value in the JSON body —
      // store it in memory so the axios interceptor uses an authoritative
      // source instead of fishing through document.cookie (which may have
      // duplicates from old sessions).
      if (response.data?.csrf_token) {
        setCsrfToken(response.data.csrf_token);
      }
    } catch (err) {
      setUser(null);
      setCsrfToken('');
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[auth] no active admin session', err?.response?.status);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // One-time cleanup of legacy localStorage token. The cookie is the new
    // source of truth — purging here means an XSS that previously had access
    // to the token can no longer find it on next page load.
    try {
      if (localStorage.getItem(LEGACY_TOKEN_KEY)) {
        localStorage.removeItem(LEGACY_TOKEN_KEY);
      }
    } catch (err) {
      // Private browsing or storage quota — nothing actionable for the user.
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[auth] legacy token cleanup skipped:', err?.message);
      }
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      // withCredentials: true so the browser stores the Set-Cookie response
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/login`,
        { email, password },
        { withCredentials: true }
      );
      const { user: userData } = response.data;
      setUser(userData);
      // Immediately call /me to fetch the authoritative CSRF token and seed
      // _inMemoryCsrf BEFORE the user clicks anything that mutates state.
      await checkAuth();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed',
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/logout`,
        {},
        { withCredentials: true }
      );
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[auth] logout request failed (clearing local state anyway):', err);
      }
    }
    setUser(null);
    setCsrfToken('');
  };

  const register = async (email, password, name) => {
    try {
      // Registration is an authenticated admin action → send cookies.
      await axios.post(
        `${BACKEND_URL}/api/admin/register`,
        { email, password, name, role: 'admin' },
        { withCredentials: true }
      );
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed',
      };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        // `token` is intentionally always null now — kept in the shape for
        // one release so legacy admin components don't crash on destructure.
        token: null,
        login,
        logout,
        register,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
